<template>
    <b-modal
            id="request-additional-parts-modal"
            title="Request additional parts"
            size="lg"
            no-footer>


        <!-- Request additional parts -->
        <template v-if="!loadingParts && !requestingParts && !newRepairKitId && !stockErrorItems.length && !confirming">
            <div class="row">
                <div class="col-12">
                    <h4 class="mb-2">Select additional parts</h4>
                    <p class="text-sm">A new repair kit will be built and delivered to you with only the parts and quantities that are selected here.</p>
                </div>
            </div>
            <div class="row">
                <div class="col-12">
                    <ul class="list-group selection-list">
                        <li class="list-group-item d-flex align-items-center justify-content-between" v-for="part in partsForDevice" :key="part.id">
                            <span>{{ part.title_with_colour }}</span>
                            <div class="quantity-controls">
                                <button class="btn btn-outline-secondary btn-sm qty-btn" type="button" @click="decrementQuantity(part)" :disabled="requestedQuantities[part.id] === 0">-</button>
                                <input
                                  type="number"
                                  class="form-control form-control-sm qty-input text-center"
                                  :value="requestedQuantities[part.id] || 0"
                                  min="0"
                                  @input="onQuantityInput(part, $event)"
                                >
                                <button class="btn btn-outline-secondary btn-sm qty-btn" type="button" @click="incrementQuantity(part)">+</button>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </template>

        <!-- Confirmation screen -->
        <template v-if="confirming && !requestingParts && !newRepairKitId && !stockErrorItems.length">
            <div class="row mb-3">
                <div class="col-12">
                  <animated-info class="mx-auto mb-4"></animated-info>
                  <h3 class="mb-2 text-center">Are you sure the below items and quantities are correct?</h3>
                </div>
            </div>
            <div class="row">
                <div class="col-12">
                    <ul class="list-group">
                        <li class="list-group-item d-flex align-items-center justify-content-between" v-for="part in selectedPartsWithQuantity" :key="part.id">
                            <span>{{ part.title_with_colour }}</span>
                            <span class="badge badge-secondary badge-pill text-white">x{{ part.quantity }}</span>
                        </li>
                    </ul>
                </div>
            </div>
        </template>

        <!-- No stock available -->
        <template v-if="stockErrorItems.length">
            <empty-state
                    class="pb-0"
                    statusIcon="error"
                    title="Stock not available"
                    subtitle="The below stock items are unavailable:">
            </empty-state>

            <div class="row mb-3">
                <div class="col-12 text-center">
                    <ul class="list-unstyled">
                        <li class="text-sm" v-for="stockErrorItem in stockErrorItems" :key="stockErrorItem">
                            {{ stockErrorItem }}
                        </li>
                    </ul>
                </div>
            </div>

            <div class="row">
                <div class="col-12 text-center">
                    <h5>Return this asset to the 'awaiting parts' location</h5>
                </div>
            </div>
        </template>

        <!-- Parts successfully requested -->
        <empty-state
                v-if="!loadingParts && !requestingParts && newRepairKitId"
                statusIcon="success"
                title="These parts are in stock"
                :subtitle="`They will be delivered to you in repair kit ${newRepairKitId}`">
        </empty-state>

        <!-- Loading parts -->
        <empty-state
                v-if="loadingParts"
                class="bg-transparent"
                statusIcon="loading"
                title="Loading available parts"
                subtitle="Please wait">
        </empty-state>

        <!-- Requesting parts -->
        <empty-state
                v-if="requestingParts"
                class="bg-transparent"
                statusIcon="loading"
                title="Checking stock of parts"
                subtitle="Please wait">
        </empty-state>

        <template v-slot:modal-footer>
            <button type="button" class="btn btn-light" @click="onCancelOrBack()" :disabled="requestingParts">{{ cancelButtonText }}</button>
            <button type="button" class="btn btn-success" @click="confirming ? requestParts() : beginConfirm()" :disabled="loadingParts || requestingParts || !hasRequestedParts" v-if="!newRepairKitId && !stockErrorItems.length">{{ confirming ? 'Confirm parts' : 'Request parts' }}</button>
        </template>

        <remove-parts-modal></remove-parts-modal>
    </b-modal>
</template>

<script>
    import {mapGetters, mapActions} from 'vuex';

    export default {
        data() {
            return {
                loadingParts: false,
                requestingParts: false,
                confirming: false,
                partsForDevice: [],
                requestedQuantities: {},
                stockErrorItems: [],
                newRepairKitId: null
            }
        },

        computed: {
            ...mapGetters([
                "job",
                "device",
                "isRefurbOrMailIn"
            ]),

            hasRequestedParts() {
                // At least one part with quantity > 0
                return Object.values(this.requestedQuantities).some(qty => qty > 0);
            },

            selectedPartsWithQuantity() {
                return this.partsForDevice
                    .filter(part => this.requestedQuantities[part.id] > 0)
                    .map(part => ({ ...part, quantity: this.requestedQuantities[part.id] }));
            },

            cancelButtonText() {
                if (this.newRepairKitId || this.stockErrorItems.length) return 'Close';
                if (this.confirming) return 'Back';
                return 'Cancel';
            }
        },

        methods: {

            ...mapActions([
                "displayToast",
                "requestAdditionalParts",
                "fetchAvailableParts"
            ]),

            onQuantityInput(part, event) {
              let val = event.target.value;
              let num = parseInt(val, 10);
              if (isNaN(num) || num < 0) num = 0;
              this.$set(this.requestedQuantities, part.id, num);
            },

            openModal() {
                this.loadingParts = true;
                this.error = null;
                this.newRepairKitId = null;
                this.confirming = false;
                this.requestedQuantities = {};
                this.partsForDevice = [];
                this.stockErrorItems = [];
                this.fetchAvailablePartsForDevice();
                this.$bvModal.show('request-additional-parts-modal')
            },

            fetchAvailablePartsForDevice() {
                let that = this;

                this.fetchAvailableParts({deviceId: this.device.id})
                    .then(response => {
                        that.loadingParts = false;
                        that.partsForDevice = response.data.data;
                        that.requestedQuantities = {};
                        for (const part of that.partsForDevice) {
                            that.$set(that.requestedQuantities, part.id, 0);
                        }
                    })
                    .catch(response => {
                        this.displayToast({text: response.response.data.messages[0], type: 'error'});
                        that.loadingParts = false;
                    });
            },

            requestParts() {
                if (this.requestingParts) return;

                this.requestingParts = true;

                const requestedPartIds = [];
                Object.entries(this.requestedQuantities).forEach(([id, qty]) => {
                  for (let i = 0; i < qty; i++) {
                    requestedPartIds.push(Number(id));
                  }
                });

                this.requestAdditionalParts({
                    deviceId: this.device.id,
                    requestedPartIds
                }).then(response => {
                    this.requestingParts = false;
                    this.newRepairKitId = response.data.data.repair_kit_id;

                }).catch(response => {
                    let errors = response.response.data.messages;

                    if (errors.missing_stock) {
                        this.stockErrorItems = errors.missing_stock;

                    } else {
                        this.displayToast({text: errors[0], type: 'error'});
                    }

                    this.requestingParts = false;
                });
            },

            incrementQuantity(part) {
                if (!this.requestedQuantities[part.id]) {
                    this.$set(this.requestedQuantities, part.id, 1);
                } else {
                    this.$set(this.requestedQuantities, part.id, this.requestedQuantities[part.id] + 1);
                }
            },
            decrementQuantity(part) {
                if (this.requestedQuantities[part.id] > 0) {
                    this.$set(this.requestedQuantities, part.id, this.requestedQuantities[part.id] - 1);
                }
            },

            beginConfirm() {
                this.confirming = true;
            },

            onCancelOrBack() {
                if (this.newRepairKitId || this.stockErrorItems.length) {
                    this.$bvModal.hide('request-additional-parts-modal');
                } else if (this.confirming) {
                    this.confirming = false;
                } else {
                    this.$bvModal.hide('request-additional-parts-modal');
                }
            }
        }
    }
</script>

<style scoped>
.quantity-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 100px;
  max-width: 140px;
}
.qty-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.qty-input {
  width: 48px;
  min-width: 48px;
  max-width: 48px;
  padding: 0 0.25rem;
  font-size: 1rem;
  background: transparent;
  border: none;
  box-shadow: none;
  outline: none;
  -webkit-appearance: none;
  -moz-appearance: textfield;
}
.qty-input::-webkit-outer-spin-button,
.qty-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.qty-input[type=number] {
  -moz-appearance: textfield;
}
.list-group-item {
  padding: 10px 15px;
}

.selection-list .list-group-item {
  padding: 10px;
}

@media only screen and (max-width: 480px) {
  .selection-list .list-group-item span {
    max-width: 150px;
  }
}
</style>
